import type {DragEndEvent, DragMoveEvent} from '@github-ui/drag-and-drop'
import {useSortable} from '@github-ui/drag-and-drop'
import {createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef} from 'react'

import {ViewerPrivileges} from '../../../../helpers/viewer-privileges'
import useBodyClass from '../../../../hooks/use-body-class'
import {useViews} from '../../../../hooks/use-views'
import {useVisibleFields} from '../../../../hooks/use-visible-fields'
import {isAddColumnId, isDragDropColumnId} from '../../column-ids'

// The half of a column header the user is dragging over.
type Side = 'left' | 'right'
// The column being dragged over and on which side.
type DragOverState = {columnId: string; side: Side}
// Contains the column associated with a drag and drop event.
type DragMetadata = {columnId: string}

// Actions and action producers
const ActionTypes = {
  SET_DRAG_TARGET: 'SET_DRAG_TARGET',
  RESET: 'RESET',
} as const
type ActionTypes = ObjectValues<typeof ActionTypes>

type Action = ResetAction | SetDragColumnTargetAction

type ResetAction = {
  type: typeof ActionTypes.RESET
}

function reset(): ResetAction {
  return {
    type: ActionTypes.RESET,
  }
}

type SetDragColumnTargetAction = {
  type: typeof ActionTypes.SET_DRAG_TARGET
  columnId: string
  side: Side
}

function setDragColumnTarget(columnId: string, side: Side): SetDragColumnTargetAction {
  return {
    type: ActionTypes.SET_DRAG_TARGET,
    columnId,
    side,
  }
}

const initialDragOverState = undefined

function dragOverStateReducer(state: DragOverState | undefined, action: Action): DragOverState | undefined {
  switch (action.type) {
    case ActionTypes.RESET: {
      return initialDragOverState
    }
    case ActionTypes.SET_DRAG_TARGET: {
      return {
        columnId: action.columnId,
        side: action.side,
      }
    }
    default:
      return state
  }
}

export const ColumnOrderDndContext = createContext<{
  dragOverState: DragOverState | undefined
  dispatch: React.Dispatch<Action>
} | null>(null)

export const ColumnOrderDndProvider: React.FC<{
  children: React.ReactNode
}> = ({children}) => {
  const [dragOverState, dispatch] = useReducer(dragOverStateReducer, initialDragOverState)
  const value = useMemo(() => {
    return {
      dragOverState,
      dispatch,
    }
  }, [dragOverState, dispatch])
  return <ColumnOrderDndContext.Provider value={value}>{children}</ColumnOrderDndContext.Provider>
}

export function useColumnReorderingDragHandlers() {
  const {moveField, visibleFields} = useVisibleFields()
  const {currentView} = useViews()
  const dispatch = useColumnOrderDndDispatch()

  // There appears to be a race condition where a drag move event is
  // is occasionally triggered after a drag end event.
  // This ref is used to ensure a move does cause a dispatch
  // after the drag has ended.
  const isDraggingRef = useRef(false)

  const onDragStart = useCallback(() => {
    isDraggingRef.current = true
  }, [])

  const pointerXRef = useRef<number>(0)
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => (pointerXRef.current = e.clientX)
    document.addEventListener('mousemove', onMouseMove)
    return () => document.removeEventListener('mousemove', onMouseMove)
  })

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      if (!e.over || !isDraggingRef.current) {
        dispatch(reset())
        return
      }
      const {columnId: overColumnId} = e.over.data.current as DragMetadata
      const side = getTargetSide(e, overColumnId, pointerXRef.current)
      dispatch(setDragColumnTarget(overColumnId, side))
    },
    [dispatch],
  )

  const onDragCancel = useCallback(() => {
    isDraggingRef.current = false
    dispatch(reset())
  }, [dispatch])

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      isDraggingRef.current = false

      const {columnId: draggingColumnId} = e.active.data.current as DragMetadata
      const overMetadata = e.over?.data.current as DragMetadata | undefined
      const overColumnId = overMetadata?.columnId

      if (!currentView || !overColumnId || draggingColumnId === overColumnId) {
        dispatch(reset())
        return
      }

      const side = getTargetSide(e, overColumnId, pointerXRef.current)

      const columnOrder = visibleFields.map(c => c.id.toString())
      const movingColumnIndex = columnOrder.indexOf(draggingColumnId)
      const overColumnIndex = columnOrder.indexOf(overColumnId)
      let newPosition: number
      if (side === 'left') {
        newPosition = movingColumnIndex < overColumnIndex ? overColumnIndex - 1 : overColumnIndex
      } else {
        newPosition = movingColumnIndex < overColumnIndex ? overColumnIndex : overColumnIndex + 1
      }

      dispatch(reset())
      const field = visibleFields.find(f => `${f.id}` === draggingColumnId)

      if (!field) {
        throw new Error('No field found for dragging column')
      }

      moveField(currentView.number, field, newPosition)
    },
    [dispatch, currentView, moveField, visibleFields],
  )

  return useMemo(() => {
    return {
      onDragStart,
      onDragMove,
      onDragEnd,
      onDragCancel,
    }
  }, [onDragStart, onDragMove, onDragEnd, onDragCancel])
}

/**
 * @param pointerX Current x-position of the mouse. This can't be obtained from the dnd-kit event because the
 * `activatorEvent.clientX` is always the _start_ of the drag. And `activatorEvent.clientX + e.delta.x` has error that
 * increases over the length of the drag.
 */
function getTargetSide(e: DragMoveEvent | DragEndEvent, columnId: string, pointerX: number): Side {
  // we have two columns that are considered special related to column reordering:
  if (isDragDropColumnId(columnId)) {
    // row-drag-handle - first column - no column can go before it
    return 'right'
  }
  if (isAddColumnId(columnId)) {
    // add-column - last column - no column can go after it
    return 'left'
  }
  const clientRect = e.over?.rect
  if (!clientRect || !(e.activatorEvent instanceof PointerEvent)) return 'right'

  const centerX = clientRect.left + clientRect.width / 2

  if (pointerX > centerX) {
    return 'right'
  } else {
    return 'left'
  }
}

/*
 * Attach handlers to header props to update dragging state based on user mouse
 * events.
 */
export function useGetDraggableHeaderProps(columnId: string, canReorder: boolean) {
  const {hasWritePermissions} = ViewerPrivileges()
  const disabled = !hasWritePermissions || canReorder === false
  const dragProps = useSortable({
    disabled,
    id: columnId,
    data: {columnId} as DragMetadata,
  })
  useBodyClass('is-dragging', dragProps.isDragging)
  return dragProps
}

/**
 * Gets the dispatch function for the ColumnOrderDndContext.
 */
const useColumnOrderDndDispatch = () => {
  const context = useContext(ColumnOrderDndContext)
  if (!context) {
    throw new Error('useColumnOrderDnd must be used within a ColumnOrderDndProvider')
  }
  return context.dispatch
}

/**
 * Gets the current drag over column state for the ColumnOrderDndContext.
 */
export function useDragOverColumnState(): DragOverState | undefined {
  const context = useContext(ColumnOrderDndContext)
  if (!context) {
    throw new Error('useDragOverColumnState must be used within a ColumnOrderDndProvider')
  }
  return context.dragOverState
}
